import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import StarRating from '../../../../../src/components/SolvPatternLibrary/StarRating';
import { getLocationRatingAndRatingCount } from '../../../../core/util/location';
import { fontFamily, fontSize } from '../../../../constants/text';
import styled from 'styled-components';
import { colors } from '../../../../constants/colors';
import { isSolvPartner } from '../../../../core/util/location';
import { isEmptyString, pluralize } from '../../../../core/util/string';
import { LEAVE_REVIEW_MODAL } from '../../Modals/LeaveAReviewTestModal';
import { analyticsTrackEvent } from '../../../../core/analytics';
import { CDP_CLICK_LEAVE_REVIEW } from '../../../../core/analytics/events';

const ClinicStars = styled(StarRating)`
  display: inline-block;
  margin-right: 11px;
`;

const ClinicRating = styled.div`
  padding-bottom: 0;
`;

const RatingLabel = styled.span`
  vertical-align: top;
  font-family: ${fontFamily.book};
  font-size: ${fontSize.mediumSmall};
  line-height: 23px;
  display: inline-block;
  -webkit-font-smoothing: antialiased;

  a {
    color: ${({ link }) => (link ? colors.mrBlueSky : colors.blueMonday)};
  }
`;

const RatingValue = styled(RatingLabel)`
  padding-right: 8px;
  color: ${colors.blueMonday};
`;

const LeaveAReviewClick = styled(RatingLabel)`
  color: ${colors.mrBlueSky};
  cursor: pointer;
  display: inline-block;

  &:before {
    content: '|';
    padding: 0 8px;
    color: ${colors.blueMonday};
  }

  &:hover {
    text-decoration: underline;
  }
`;

const Bundle = styled.div`
  display: inline-block;
  white-space: nowrap;
`;

const hasRatings = ratingCount => !isEmptyString(ratingCount) && ratingCount > 0;

class Rating extends PureComponent {
  constructor(props) {
    super(props);
    const { rating, ratingCount } = getLocationRatingAndRatingCount(props.location);
    this.rating = parseFloat((Math.round(rating * 10) / 10).toFixed(2));
    this.ratingCount = ratingCount;
  }

  leaveReviewClick = () => {
    const { location, showModal } = this.props;
    showModal(LEAVE_REVIEW_MODAL);
    analyticsTrackEvent(CDP_CLICK_LEAVE_REVIEW, {
      isSolvPartner: isSolvPartner(location),
    });
  };

  renderWriteReviewCta = containsRatings => (
    <LeaveAReviewClick data-testid="writereviewcta" onClick={this.leaveReviewClick}>
      {containsRatings ? 'Write a review' : 'Be the first to write a review!'}
    </LeaveAReviewClick>
  );

  render = () => {
    const {
      props: { className },
      rating,
      ratingCount,
    } = this;

    const containsRatings = hasRatings(ratingCount);
    const displayedRating = rating || 0;

    return (
      <div className={className}>
        <ClinicRating
          itemScope={containsRatings}
          itemType={containsRatings ? 'http://schema.org/AggregateRating' : undefined}
          itemProp={containsRatings ? 'aggregateRating' : undefined}
        >
          {containsRatings && <RatingValue itemProp="ratingValue">{displayedRating}</RatingValue>}
          <ClinicStars
            size={fontSize.default}
            rating={displayedRating}
            identifier={`${className}${displayedRating}${ratingCount}`}
          />
          <Bundle>
            <RatingLabel link={containsRatings}>
              <a href="#reviews">
                <span itemProp={containsRatings ? 'ratingCount' : undefined}>
                  {ratingCount || 'No'}{' '}
                </span>
                {pluralize(ratingCount, 'Rating')}
              </a>
            </RatingLabel>
            {!this.props.shouldHideWriteReviewCta && this.renderWriteReviewCta(containsRatings)}
          </Bundle>
        </ClinicRating>
      </div>
    );
  };
}

Rating.propTypes = {
  location: PropTypes.object.isRequired,
  className: PropTypes.string,
  shouldHideWriteReviewCta: PropTypes.bool,
  showModal: PropTypes.func,
};

export default Rating;
